import React from "react";
import { debounce } from "lodash";
import styled from "styled-components";
import { Section } from "../../../components/Core";

import EyeTunnel from '../../../components/TheEye/EyeTunnel';
import HeaderPanel from "../../../components/LuxContent/HeaderPanel";
import ContentPanel from "../../../components/LuxContent/ContentPanel";
import { parseToHsl, setHue } from 'polished';
import LuxWeb3Context from '../../../context/LuxWeb3Context';
import { buildFluidHash } from '../../../utils/fluidBgHash';
import { renderEyeToCanvas, meltEyeIntoMount } from '../../../utils/fluidBgEye';
import { withTunedGLContext, getFluidRenderScale } from '../../../utils/fluidBgGL';
import FLUID_BG_SETTINGS from '../../../utils/fluidBgConfig';
import theme from '../../../utils/theme';
import FluidTuner from '../../../components/FluidTuner/FluidTuner';

// BACKGROUND : re-hue the saved background ramp to this session's primary
// cult color (cultPalette[0] — the button accent), so the animation always
// matches whatever the random palette landed on. Rotating hue only keeps
// the tuned lightness/saturation shape of the ramp; black stays black.
// (The ?tune overlay intentionally shows the raw saved colors.)
const matchBgToPalette = (settings) => ({
  ...settings,
  colors: settings.colors.map(c =>
    c === '#000000' ? c : setHue(parseToHsl(theme.cultPalette[0]).hue, c)),
});

// TUNER : hidden for now — the saved look lives in src/utils/fluidBgConfig.js.
// To tune again, either flip this to true or just visit the site with ?tune
// appended (e.g. localhost:9001/?tune) — no code edit needed.
const SHOW_FLUID_TUNER = false;

const EntryWrap = styled(Section)`
  position: relative;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  padding-top: 0;

  svg {
    transform: translate(-50%, -50%);
  }

  .fluid-bg-host {
    position: absolute;
    inset: 0;
    z-index: 0;
    transform-origin: center;
  }

  .content-wrap {
    position: relative;
    z-index: 1;
  }

  &.enlightenment-mode {
    .outer-wrap {
      opacity: 0;
      pointer-events: none;
      transition: all 0.25s ease-in-out;
    }
  }
`;



class EntryEye extends React.Component {
  static contextType = LuxWeb3Context;
  constructor(props) {
    super(props);
    this.state = {
      height: '100vh',
      width: 'auto'
    }

    this.bgHostRef = React.createRef();
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', debounce(this.handleResize, 300) )
    this.handleResize();
    this.context.getShopifyProducts();
    this.mountFluidBg();
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.fluidBg) {
      this.fluidBg.destroy();
      this.fluidBg = null;
    }
  }

  // BACKGROUND : Mount the fluid-bg generative background into the
  // .fluid-bg-host container below (filling EntryWrap, behind .content-wrap
  // via z-index). Not `fixed` — that pins it at z-index -1 behind the whole
  // page, which an opaque body/html background paints over. Imported
  // dynamically (and via the dist path) because the package is ESM-only
  // with an `exports` map and registers browser globals, which Gatsby's
  // SSR build / webpack 4 can't handle as a static top-level import.
  mountFluidBg() {
    import("fluid-bg/dist/core.js").then(({ fluidBackground, mountNative }) => {
      if (this.unmounted || !this.bgHostRef.current) return;
      const settings = matchBgToPalette(FLUID_BG_SETTINGS);
      const eye = settings.eye;

      // EYE MELT : when the saved config wants the eye as a 4th engine
      // layer, mount via mountNative (exposes the GL internals the melt
      // needs) and inject it — see src/utils/fluidBgEye.js
      if (eye && eye.melt) {
        try {
          this.fluidBg = withTunedGLContext(() =>
            mountNative(this.bgHostRef.current, buildFluidHash(settings)));
          renderEyeToCanvas(eye).then(cv => {
            if (!this.unmounted && this.fluidBg) {
              meltEyeIntoMount(this.fluidBg, cv, eye);
            }
          });
          return;
        } catch (e) {
          // WebGL unavailable — fall through to fluidBackground, which
          // has its own iframe fallback (no eye there)
        }
      }

      this.fluidBg = withTunedGLContext(() => fluidBackground(this.bgHostRef.current, {
        hash: buildFluidHash(settings)
      }));
    });
  }


  // UTIL : Set New Screen-proper Dimensions on EyeTunnel
  setDimensions(dimensions) {
    this.setState(dimensions);
  }

  // UTIL : Resize window to properly fit the eye tunnel animation
  handleResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const ratio = w/h;
    // console.log('RESIZE EVENT -- Size: ', window.innerWidth, 'x', window.innerHeight, ' RATIO: ', ratio);

    if ( ratio > 1.7777777 ) {
      this.setDimensions({
        height: 'auto',
        width: '100vw'
      })
    }
    else {
      this.setDimensions({
        height: '100vh',
        width: 'auto'
      })
    }
  }

  render() {
    const { enlightenmentMode, isMenuOpen, location } = this.context;
    const enlightenmentClass = enlightenmentMode ? 'enlightenment-mode' : '';
    const dimensions = this.state;
    const isSSR = typeof window === 'undefined';

    // RENDER SCALE : shrink the host box and scale it back up with CSS on
    // browsers/devices that need it — see getFluidRenderScale() for why.
    const fluidScale = isSSR ? 1 : getFluidRenderScale();
    const fluidHostStyle = fluidScale < 1 ? {
      width: `${fluidScale * 100}%`,
      height: `${fluidScale * 100}%`,
      top: `${(1 - fluidScale) * 50}%`,
      left: `${(1 - fluidScale) * 50}%`,
      transform: `scale(${1 / fluidScale})`,
    } : undefined;

    return(
      <>
        { !isSSR && 
          <EntryWrap id="entry-eye" className={ !!location ? `page-${location} ${enlightenmentClass}` : `page-home ${enlightenmentClass}`}>

            {/* BACKGROUND : fluid-bg, mounted into this host in componentDidMount */}
            {/* <EyeTunnel dimensions={dimensions} /> */}
            <div className="fluid-bg-host" ref={this.bgHostRef} style={fluidHostStyle} />


            {/* CONTENT */}
            <div className="content-wrap">

              {/* HEADER */}
              <HeaderPanel />
              
              <div className="outer-wrap">
                {/* Passedas props on purpose so we can limit unnecessary 
                    renders from context always triggering in lower levels */}
                <ContentPanel isMenuOpen={isMenuOpen} />
              </div>
            </div>

          </EntryWrap>
        }

        {/* TUNER : dev overlay for dialing in the fluid background — enable
            via SHOW_FLUID_TUNER above or a ?tune query param */}
        { !isSSR && (SHOW_FLUID_TUNER || window.location.search.indexOf('tune') >= 0) &&
          <FluidTuner /> }
      </>
    )
  };
}

export default EntryEye;
